/**
 * First-leak-report celebration modal — pops once when a free user
 * crosses the 25-signal threshold that unlocks their first leak report.
 *
 * Trigger: `billing_status.signal_count >= signals_for_first_report
 * AND !billing_status.first_leak_report_milestone_shown`.
 *
 * Mounted via `<FirstLeakReportModalProvider>` in App.tsx. It auto-
 * fires by watching the `["billing-status"]` TanStack query — the
 * provider doesn't need an explicit trigger from any other component.
 *
 * Dismiss flow:
 *   1. User clicks "View your leak report" → navigates to /skills/focus
 *      (the leak-report sub-view), modal closes, POST acknowledge flips the
 *      durable flag.
 *   2. User clicks the X → modal closes, POST acknowledge flips the flag.
 *   3. User clicks the backdrop → same as X.
 *
 * Once the flag is True on the backend, the next `/billing/status`
 * read returns `first_leak_report_milestone_shown: True` and this
 * modal will never re-trigger for the user.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  acknowledgeFirstLeakReportMilestone,
  getBillingStatus,
  shouldShowFirstLeakReportCelebration,
} from "@/features/billing/lib/billing";
import { ICON_STROKE, X } from "@/shared/components/icons";

export function FirstLeakReportModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { data: status } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
  });
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Ref guard so we don't auto-open the modal multiple times if the
  // query refetches while it's already showing.
  const hasOpenedRef = useRef(false);

  // Auto-open when billing status says we should celebrate.
  useEffect(() => {
    if (!status) return;
    if (shouldShowFirstLeakReportCelebration(status) && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setOpen(true);
    }
  }, [status]);

  const acknowledge = async () => {
    setOpen(false);
    try {
      await acknowledgeFirstLeakReportMilestone();
    } catch {
      // Silent failure — worst case the modal re-fires next session,
      // which is a minor annoyance not a bug.
    }
    // Refetch so the chip + Settings + anything else watching the
    // query reflect the new milestone_shown=true state.
    queryClient.invalidateQueries({ queryKey: ["billing-status"] });
  };

  return (
    <>
      {children}
      {open && <FirstLeakReportModalView onClose={acknowledge} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// The modal itself
// ---------------------------------------------------------------------------

function FirstLeakReportModalView({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  const goToLeakReport = async () => {
    // The leak report lives on the Focus sub-view, not /skills (Standing).
    navigate("/skills/focus");
    await onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-2xl border border-accent/40 bg-surface relative shadow-[0_28px_60px_-20px_rgba(124,147,241,0.7)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="text-center space-y-2">
            <div className="text-4xl">🎉</div>
            <h2 className="text-2xl font-semibold text-text tracking-tight">
              Your first leak report is ready
            </h2>
            <p className="text-sm text-text-muted leading-relaxed">
              You've drilled enough spots to build a real skill profile.
              Your leak report shows where to focus next: the patterns
              costing you the most EV.
            </p>
          </div>

          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3 text-xs text-text-muted text-center">
            <span className="font-medium text-text">New — close the loop:</span>{" "}
            track your real sessions and have the coach debrief them. You drill
            to get sharper; your results tell the coach what to fix next.
          </div>

          <div className="rounded-lg border border-border bg-bg/40 p-3 text-xs text-text-muted text-center">
            Free accounts wrap up here — subscribe to keep drilling with the
            coach and unlock ongoing reports. Your saved report, tracker,
            quizzes, and study sheets stay free.
          </div>
        </div>

        <div className="p-6 pt-2 border-t border-border/40">
          <button
            type="button"
            onClick={goToLeakReport}
            className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
          >
            View your leak report
          </button>
        </div>
      </div>
    </div>
  );
}
